import { Archive, ExternalLink, FileSearch, GitPullRequestArrow, ShieldAlert, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import type {
  FallbackPromotionControlTowerModel,
  FallbackPromotionItem,
  ScopedFallbackPromotionModel,
} from '@/features/validation/fallback-promotion-control-tower-model'
import {
  buildFallbackPromotionControlTowerModelFromObservations,
  parseFallbackPromotionObservationsPayload,
} from '@/features/validation/fallback-promotion-control-tower-model'

const STATUS_CLASS: Record<FallbackPromotionControlTowerModel['status'], string> = {
  pass: 'border-success/25 bg-success/10 text-success dark:text-success',
  blocked: 'border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-300',
  review: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  ready: 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-300',
}

const ITEM_CLASS: Record<FallbackPromotionItem['status'], string> = {
  candidate: 'text-sky-600 dark:text-sky-300',
  quarantine: 'text-amber-600 dark:text-amber-300',
  blocked: 'text-red-600 dark:text-red-300',
  'pr-ready': 'text-success dark:text-success',
  archived: 'text-muted-foreground',
}

type ControlTowerFilter = 'all' | FallbackPromotionControlTowerModel['sections'][number]['id']

const FILTERS: Array<{ id: ControlTowerFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'candidate-facts', label: 'Needs evidence' },
  { id: 'quarantine', label: 'Quarantined' },
  { id: 'promotion-blocked', label: 'Blocked' },
  { id: 'pr-ready', label: 'Ready for patch' },
]

const ITEM_STATUS_LABEL: Record<FallbackPromotionItem['status'], string> = {
  candidate: 'needs-evidence',
  quarantine: 'quarantined',
  blocked: 'blocked',
  'pr-ready': 'ready-for-patch',
  archived: 'archived',
}

const PROMOTION_CONDITIONS = [
  'schema validation passed',
  'runtime validation passed',
  'repeated evidence confirmed',
  'reviewer or rule approved',
] as const

export function FallbackPromotionControlTowerPanel({
  models,
  onNavigate,
  liveObservationUrl,
  pollIntervalMs = 30_000,
}: {
  models: ScopedFallbackPromotionModel[]
  onNavigate?: (path: readonly (string | number)[]) => void
  liveObservationUrl?: string
  pollIntervalMs?: number
}) {
  const liveModel = useLiveObservationModel(liveObservationUrl, pollIntervalMs)
  const visibleModels = useMemo(
    () => liveModel ? [...models, liveModel] : models,
    [liveModel, models],
  )

  if (visibleModels.length === 0 && !liveObservationUrl) return null

  return (
    <section
      data-testid="fallback-promotion-control-tower-panel"
      className="border-b border-border bg-background"
    >
      <div className="px-3 py-2 flex flex-wrap items-center gap-2">
        <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" />
        <div className="font-medium text-[11px] text-foreground">
          Fallback promotion control tower
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
          <span>read-only</span>
          <span>reviewed PR handoff only</span>
        </div>
      </div>

      <div className="px-3 pb-3 space-y-2">
        {liveObservationUrl && !liveModel ? (
          <LiveObservationEmptyState liveObservationUrl={liveObservationUrl} />
        ) : null}
        {visibleModels.map((scoped) => (
          <ControlTowerScope key={`${scoped.scope}:${scoped.path.join('.')}`} scoped={scoped} onNavigate={onNavigate} />
        ))}
      </div>
    </section>
  )
}

function LiveObservationEmptyState({ liveObservationUrl }: { liveObservationUrl: string }) {
  return (
    <div className="rounded border border-border/70 bg-muted/20 px-2 py-2 text-[11px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-muted-foreground">Live observations</span>
        <span className="rounded border border-border/70 bg-background/70 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          pending
        </span>
        <a
          href={liveObservationUrl}
          target="_blank"
          rel="noreferrer"
          className="ml-auto inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
        >
          <FileSearch className="h-3 w-3" />
          Inspect evidence
          <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>
      <div className="mt-1 text-muted-foreground">
        Waiting for fallback evidence from the observation stream.
      </div>
    </div>
  )
}

function useLiveObservationModel(
  liveObservationUrl: string | undefined,
  pollIntervalMs: number,
): ScopedFallbackPromotionModel | null {
  const [model, setModel] = useState<ScopedFallbackPromotionModel | null>(null)

  useEffect(() => {
    if (!liveObservationUrl) return

    const url = liveObservationUrl
    let cancelled = false
    async function tick() {
      try {
        const response = await fetch(url)
        if (!response.ok) {
          if (!cancelled) setModel(null)
          return
        }
        const payload = await response.json()
        if (cancelled) return
        const observations = parseFallbackPromotionObservationsPayload(payload)
        const liveControlTower = buildFallbackPromotionControlTowerModelFromObservations(
          observations,
          { evidenceUrl: url },
        )
        setModel(liveControlTower ? {
          scope: 'Live observations',
          path: [],
          model: liveControlTower,
        } : null)
      } catch {
        if (!cancelled) setModel(null)
      }
    }

    tick()
    const interval = setInterval(tick, pollIntervalMs)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [liveObservationUrl, pollIntervalMs])

  return model
}

function ControlTowerScope({
  scoped,
  onNavigate,
}: {
  scoped: ScopedFallbackPromotionModel
  onNavigate?: (path: readonly (string | number)[]) => void
}) {
  const { model } = scoped
  const [filter, setFilter] = useState<ControlTowerFilter>('all')
  const [selectedItem, setSelectedItem] = useState<FallbackPromotionItem | null>(null)
  const nonEmptySections = model.sections.filter((section) => section.items.length > 0)
  const visibleSections = nonEmptySections.filter((section) => filter === 'all' || section.id === filter)

  return (
    <div className="rounded border border-border/70 bg-muted/20">
      <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-2 py-1.5">
        <span className="font-mono text-[10px] text-muted-foreground">{scoped.scope}</span>
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CLASS[model.status]}`}>
          {model.status}
        </span>
        <div className="ml-auto text-[10px] text-muted-foreground tabular-nums">
          needs-evidence {model.summary.candidateFacts} · quarantined {model.summary.quarantinedAntiPatterns} · blocked{' '}
          {model.summary.promotionBlocked} · ready-for-patch {model.summary.prReadyValidatedCandidates}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-border/50 px-2 py-1.5">
        {FILTERS.map((item) => {
          const count = item.id === 'all'
            ? nonEmptySections.reduce((total, section) => total + section.items.length, 0)
            : sectionCount(model, item.id)
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
              className={[
                'inline-flex h-6 items-center gap-1 rounded border px-2 text-[10px] transition-colors',
                filter === item.id
                  ? 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              <span>{item.label}</span>
              <span className="font-mono tabular-nums">{count}</span>
            </button>
          )
        })}
      </div>

      <ReadOnlyActions model={model} />
      <PromotionConditions />

      {nonEmptySections.length === 0 ? (
        <div className="px-2 py-2 text-[11px] text-muted-foreground">
          No fallback promotion items are pending for this scope.
        </div>
      ) : (
        <div className="grid gap-2 p-2 xl:grid-cols-2">
          {visibleSections.map((section) => (
            <div key={section.id} className="rounded border border-border/60 bg-background/80">
              <div className="border-b border-border/50 px-2 py-1.5 text-[10px] font-medium text-muted-foreground">
                {section.title}
              </div>
              <ul className="divide-y divide-border/50">
                {section.items.map((item) => (
                  <ControlTowerItem
                    key={item.id}
                    item={item}
                    scopePath={scoped.path}
                    onNavigate={onNavigate}
                    onInspect={() => setSelectedItem(item)}
                  />
                ))}
              </ul>
            </div>
          ))}
          {visibleSections.length === 0 ? (
            <div className="rounded border border-border/60 bg-background/80 px-2 py-2 text-[11px] text-muted-foreground">
              No items match this filter.
            </div>
          ) : null}
        </div>
      )}
      <EvidenceDetailDrawer
        item={selectedItem}
        actions={model.actions}
        scope={scoped.scope}
        onClose={() => setSelectedItem(null)}
        onNavigate={onNavigate}
        scopePath={scoped.path}
      />
    </div>
  )
}

function PromotionConditions() {
  return (
    <div
      data-testid="fallback-promotion-conditions"
      className="border-b border-border/50 px-2 py-1.5 text-[10px] text-muted-foreground"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-semibold uppercase text-foreground">Promotion conditions</span>
        {PROMOTION_CONDITIONS.map((condition) => (
          <span
            key={condition}
            className="rounded border border-border bg-background px-1.5 py-0.5"
          >
            {condition}
          </span>
        ))}
      </div>
      <div className="mt-1">
        Runtime-validated fallback evidence is not canonical ontology until these gates pass.
      </div>
    </div>
  )
}

function ReadOnlyActions({ model }: { model: FallbackPromotionControlTowerModel }) {
  const actions = model.actions
  if (!actions) return null

  return (
    <div className="flex flex-wrap gap-1.5 border-b border-border/50 px-2 py-1.5 text-[10px]">
      <ActionLink href={actions.evidenceUrl} icon={<FileSearch className="h-3 w-3" />} label="Inspect evidence" />
      <ActionLink href={actions.reviewedPrUrl} icon={<GitPullRequestArrow className="h-3 w-3" />} label="Open reviewed PR" />
      <ActionLink href={actions.quarantineArchiveUrl} icon={<Archive className="h-3 w-3" />} label="Archive quarantine" />
    </div>
  )
}

function ActionLink({
  href,
  icon,
  label,
}: {
  href?: string
  icon: React.ReactNode
  label: string
}) {
  if (!href) return null
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-muted-foreground hover:text-foreground"
    >
      {icon}
      {label}
      <ExternalLink className="h-2.5 w-2.5" />
    </a>
  )
}

function ControlTowerItem({
  item,
  scopePath,
  onNavigate,
  onInspect,
}: {
  item: FallbackPromotionItem
  scopePath: Array<string | number>
  onNavigate?: (path: readonly (string | number)[]) => void
  onInspect: () => void
}) {
  const path = item.path ?? pathFromItem(item, scopePath)
  const canNavigate = Boolean(path && onNavigate)

  return (
    <li className="px-2 py-2 text-[11px]">
      <div className="flex items-start gap-2">
        <span className={`mt-1 text-[8px] leading-none ${ITEM_CLASS[item.status]}`}>
          ●
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">{item.title}</span>
            <span className="rounded border border-border px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
              {ITEM_STATUS_LABEL[item.status]}
            </span>
            {item.reviewStatus ? (
              <span className="rounded border border-border px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
                {item.reviewStatus}
              </span>
            ) : null}
            {typeof item.confidence === 'number' ? (
              <span className="font-mono text-[10px] text-muted-foreground">
                {Math.round(item.confidence * 100)}%
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-muted-foreground">{item.message}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {item.jsonPath ? (
              <span className="font-mono text-[10px] text-primary">{item.jsonPath}</span>
            ) : null}
            {item.blockName ? (
              <span className="font-mono text-[10px] text-muted-foreground">block:{item.blockName}</span>
            ) : null}
            {item.actionFlowName ? (
              <span className="font-mono text-[10px] text-muted-foreground">flow:{item.actionFlowName}</span>
            ) : null}
            {item.patchPlanId ? (
              <span className="rounded border border-sky-500/25 bg-sky-500/5 px-1.5 py-0.5 font-mono text-[10px] text-sky-700">
                patchPlan:{item.patchPlanId}
              </span>
            ) : null}
            <button
              type="button"
              onClick={onInspect}
              className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
            >
              Evidence detail
            </button>
            {canNavigate && path ? (
              <button
                type="button"
                onClick={() => onNavigate?.(path)}
                className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
              >
                JSON path
              </button>
            ) : null}
            {item.prUrl ? (
              <a
                href={item.prUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
              >
                reviewed PR
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  )
}

function EvidenceDetailDrawer({
  item,
  actions,
  scope,
  onClose,
  onNavigate,
  scopePath,
}: {
  item: FallbackPromotionItem | null
  actions?: FallbackPromotionControlTowerModel['actions']
  scope: string
  onClose: () => void
  onNavigate?: (path: readonly (string | number)[]) => void
  scopePath: Array<string | number>
}) {
  if (!item) return null

  const path = item.path ?? pathFromItem(item, scopePath)
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20" role="presentation">
      <aside
        role="dialog"
        aria-label="Evidence detail"
        className="h-full w-full max-w-[420px] overflow-auto border-l border-border bg-background shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">{scope}</div>
            <h3 className="mt-1 text-sm font-semibold text-foreground">{item.title}</h3>
            <div className={`mt-1 text-[11px] font-medium ${ITEM_CLASS[item.status]}`}>
              {item.status}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close evidence detail"
            onClick={onClose}
            className="rounded border border-border p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="space-y-3 p-4 text-[11px]">
          <DetailBlock label="Evidence">
            <p className="text-muted-foreground">{item.message}</p>
          </DetailBlock>

          <DetailGrid
            rows={[
              ['Review status', item.reviewStatus],
              ['Confidence', typeof item.confidence === 'number' ? `${Math.round(item.confidence * 100)}%` : undefined],
              ['Source', item.source],
              ['JSON path', item.jsonPath],
              ['Block', item.blockName],
              ['ActionFlow', item.actionFlowName],
            ]}
          />

          {item.patchPlanId ? (
            <DetailBlock label="PatchPlan">
              <div className="rounded border border-sky-500/25 bg-sky-500/10 px-2 py-2">
                <div className="font-mono text-[10px] text-sky-700 dark:text-sky-300">{item.patchPlanId}</div>
                <div className="mt-1 text-muted-foreground">
                  Reviewed PR handoff required. Canonical writes stay disabled in Studio.
                </div>
              </div>
            </DetailBlock>
          ) : null}

          <div className="flex flex-wrap gap-1.5">
            {actions?.evidenceUrl ? (
              <ActionLink href={actions.evidenceUrl} icon={<FileSearch className="h-3 w-3" />} label="Inspect evidence" />
            ) : null}
            <ActionLink href={item.prUrl ?? actions?.reviewedPrUrl} icon={<GitPullRequestArrow className="h-3 w-3" />} label="Open reviewed PR" />
            {path && onNavigate ? (
              <button
                type="button"
                onClick={() => onNavigate(path)}
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-muted-foreground hover:text-foreground"
              >
                JSON path
              </button>
            ) : null}
          </div>

          <div className="rounded border border-border bg-muted/20 px-2 py-2 text-muted-foreground">
            Canonical write is intentionally not available here. Promotion happens only through reviewed PR handoff.
          </div>
        </div>
      </aside>
    </div>
  )
}

function DetailBlock({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <section>
      <div className="mb-1 font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
      {children}
    </section>
  )
}

function DetailGrid({ rows }: { rows: Array<[string, string | undefined]> }) {
  const visibleRows = rows.filter((row): row is [string, string] => Boolean(row[1]))
  if (visibleRows.length === 0) return null

  return (
    <div className="rounded border border-border/70">
      {visibleRows.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[110px_1fr] border-b border-border/50 last:border-b-0">
          <div className="bg-muted/20 px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
            {label}
          </div>
          <div className="min-w-0 px-2 py-1.5 font-mono text-[10px] text-foreground break-words">
            {value}
          </div>
        </div>
      ))}
    </div>
  )
}

function sectionCount(
  model: FallbackPromotionControlTowerModel,
  id: FallbackPromotionControlTowerModel['sections'][number]['id'],
): number {
  return model.sections.find((section) => section.id === id)?.items.length ?? 0
}

function pathFromItem(
  item: FallbackPromotionItem,
  scopePath: Array<string | number>,
): Array<string | number> | null {
  if (item.blockName && item.actionFlowName) {
    return ['blocks', item.blockName, 'flow', 'flows', item.actionFlowName]
  }
  if (item.blockName) return ['blocks', item.blockName]
  if (item.jsonPath) return jsonPathToPath(item.jsonPath)
  return scopePath.length > 0 ? scopePath : null
}

function jsonPathToPath(path: string): Array<string | number> | null {
  if (!path.startsWith('$')) return null
  const parts: Array<string | number> = []
  const pattern = /\.([A-Za-z_$][\w$]*)|\["([^"]+)"\]|\[(\d+)\]/g
  for (const match of path.matchAll(pattern)) {
    if (match[1]) parts.push(match[1])
    else if (match[2]) parts.push(match[2])
    else if (match[3]) parts.push(Number(match[3]))
  }
  return parts.length > 0 ? parts : null
}
